import { Dispatch, SetStateAction, useCallback, useContext } from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import Cookies from 'js-cookie';

import { sendGA4Event } from 'app/lib/google';
import { getCookiebotConsent } from 'app/shared/utils/useCookiebotConsent';
import { AuthContext } from 'app/shared/context/Auth';

const SENSITIVE_QUERY_PARAMS = [
  'token',
  'reset_password_token',
  'ea_share_token',
  'email',
];

const sanitizeTrackingUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    SENSITIVE_QUERY_PARAMS.forEach((param) =>
      parsed.searchParams.delete(param)
    );
    return parsed;
  } catch {
    return null;
  }
};

export const useTrackAnalyticsEvent = (
  analytics: AnalyticsBrowser,
  eventsCalled: string[],
  setEventsCalled: Dispatch<SetStateAction<string[]>>
) => {
  const { visitorType } = useContext(AuthContext);

  return useCallback(
    (
      eventDescription: string,
      options: {
        [key: string]: any;
      },
      trackOnce = false,
      eventIdentifier = ''
    ) => {
      const cookieConsent = getCookiebotConsent();

      if (trackOnce) {
        const eventIdentifierString = eventIdentifier.toString();
        if (eventsCalled.includes(eventDescription + eventIdentifierString)) {
          return;
        } else {
          // Builds on the latest list, since one handler can send several track-once events
          setEventsCalled((previous) => [
            ...previous,
            eventDescription + eventIdentifierString,
          ]);
        }
      }

      // The Braze ID describes the user, not the event, so it's sent in context.traits (as the backend
      // does) instead of in the event's properties
      const { braze_external_id: brazeExternalId, ...eventProperties } =
        options;

      const cookies = Cookies.get();
      const {
        ttclid,
        tt_test_id,
        ScCid, // Snapchat cookie that we set based on query params
        _scid, // Snapchat cookie that may or may not have gotten automatically set
        _fbp, // Facebook browser id cookie, automatically set by the FB pixel script if/when it loads
        _fbc, // Facebook click id cookie, automatically set by the FB pixel script when a visitor arrives via FB ad click
      } = cookies;

      const ga4SessionKey = Object.keys(cookies).find((key: string) =>
        /^_ga_/.test(key)
      );
      const ga_session_id =
        ga4SessionKey &&
        cookies[ga4SessionKey]?.match(/GS\d\.\d\.(?<session_id>\d+)\..*/)?.[1];

      const analyticsProperties: { [key: string]: any } = {
        ...eventProperties,
        visitor_type: visitorType,
        ttclid,
        tt_test_id,
        ga_session_id,
        ScCid,
        _scid,
        fbp: _fbp,
        fbc: _fbc,
      };

      const sanitizedUrl = sanitizeTrackingUrl(window.location.href);
      const context = {
        ...(sanitizedUrl && {
          page: {
            url: sanitizedUrl.toString(),
            search: sanitizedUrl.search,
          },
        }),
        ...(brazeExternalId && {
          traits: { braze_external_id: brazeExternalId },
        }),
      };

      analytics.track(eventDescription, analyticsProperties, {
        consent: {
          categoryPreferences: {
            necessary: cookieConsent.necessaryConsent,
            preferences: cookieConsent.preferencesConsent,
            statistics: cookieConsent.statisticsConsent,
            marketing: cookieConsent.marketingConsent,
          },
        },
        ...(Object.keys(context).length > 0 && { context }),
      });
      sendGA4Event(eventDescription, analyticsProperties);
    },
    [analytics, visitorType, eventsCalled]
  );
};
